import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

type ArchetypeId = "fears" | "anger" | "confidence" | "jealousy" | "adaptation" | "grief" | "autonomy";

const ARCHETYPE_LABELS: Record<ArchetypeId, string> = {
  fears: "Свет в темноте",
  anger: "Сердитый дракон",
  confidence: "Маленький герой",
  jealousy: "Большое сердце",
  adaptation: "Новый мир",
  grief: "Потерянная звезда",
  autonomy: "Внутренний компас",
};

const ARCHETYPE_SUBTITLES: Record<ArchetypeId, string> = {
  fears: "Помогаем справиться со страхами и тревогой",
  anger: "Учимся понимать и проживать злость",
  confidence: "Поддерживаем уверенность и смелость",
  jealousy: "Помогаем принять ревность и изменения в семье",
  adaptation: "Мягкая адаптация к новому окружению",
  grief: "Бережно проживаем грусть и утрату",
  autonomy: "Учимся делать выбор и чувствовать самостоятельность",
};

const ARCHETYPE_EMOJIS: Record<ArchetypeId, string> = {
  fears: "🌙",
  anger: "🔥",
  confidence: "🌱",
  jealousy: "❤️",
  adaptation: "🚪",
  grief: "⭐",
  autonomy: "🧭",
};

// Map Q1 option index → primary archetype
const Q1_ARCHETYPE_MAP: ArchetypeId[] = [
  "fears", "anger", "confidence", "grief", "adaptation", "jealousy", "autonomy",
];

// Q2: primary and secondary archetype per option, used for context-aware coloring
const Q2_COLOR_MAP: { primary: ArchetypeId; secondary?: ArchetypeId }[] = [
  { primary: "fears", secondary: "grief" },        // Перед сном
  { primary: "adaptation", secondary: "fears" },    // В новых местах
  { primary: "confidence", secondary: "anger" },    // В общении с детьми
  { primary: "jealousy", secondary: "anger" },      // Дома с родителями
  { primary: "anger" },                              // После конфликтов
  { primary: "grief", secondary: "fears" },          // При расставании
  { primary: "adaptation", secondary: "jealousy" }, // При переменах
];

// Map Q3 option index → archetype
const Q3_ARCHETYPE_MAP: ArchetypeId[] = [
  "fears", "anger", "confidence", "adaptation", "jealousy", "grief", "autonomy",
];

// Q1 options
const Q1_OPTIONS = [
  { label: "Тревожится или боится", scores: { fears: 6 } },
  { label: "Вспышки злости", scores: { anger: 6 } },
  { label: "Замкнутость или стеснительность", scores: { confidence: 6 } },
  { label: "Часто грустит", scores: { grief: 6 } },
  { label: "Сопротивляется новому", scores: { adaptation: 6 } },
  { label: "Ревнует", scores: { jealousy: 6 } },
  { label: "Стремится всё контролировать / спорит", scores: { autonomy: 6 } },
] as const;

// Q2 options (multiple select)
const Q2_OPTIONS = [
  { label: "Перед сном", scores: { fears: 2, grief: 1 } },
  { label: "В новых местах", scores: { adaptation: 2, fears: 1 } },
  { label: "В общении с детьми", scores: { confidence: 2, anger: 1 } },
  { label: "Дома с родителями", scores: { jealousy: 2, anger: 1 } },
  { label: "После конфликтов", scores: { anger: 2 } },
  { label: "При расставании", scores: { grief: 2, fears: 1 } },
  { label: "При переменах (сад, школа, младший)", scores: { adaptation: 2, jealousy: 1, grief: 1 } },
] as const;

// Q3 options
const Q3_OPTIONS = [
  { label: "Чтобы стало спокойнее", scores: { fears: 3 } },
  { label: "Чтобы легче выражал эмоции", scores: { anger: 3 } },
  { label: "Чтобы чувствовал себя увереннее", scores: { confidence: 3 } },
  { label: "Чтобы быстрее адаптировался", scores: { adaptation: 3 } },
  { label: "Чтобы в семье стало мягче", scores: { jealousy: 3 } },
  { label: "Чтобы легче проживал грусть", scores: { grief: 3 } },
  { label: "Чтобы учился делать выбор", scores: { autonomy: 3 } },
] as const;

type Scores = Partial<Record<ArchetypeId, number>>;

function mergeScores(base: Record<ArchetypeId, number>, add: Scores) {
  for (const [k, v] of Object.entries(add)) {
    base[k as ArchetypeId] += v!;
  }
}

function computeResult(
  q1: number,
  q2: number[],
  q3: number
): { primary: ArchetypeId; secondary: ArchetypeId | null } {
  const scores: Record<ArchetypeId, number> = {
    fears: 0, anger: 0, confidence: 0, jealousy: 0,
    adaptation: 0, grief: 0, autonomy: 0,
  };

  mergeScores(scores, Q1_OPTIONS[q1].scores);
  for (const idx of q2) mergeScores(scores, Q2_OPTIONS[idx].scores);
  mergeScores(scores, Q3_OPTIONS[q3].scores);

  const q1Label = Q1_OPTIONS[q1].label;
  const q2Labels = q2.map((i) => Q2_OPTIONS[i].label);
  const q3Label = Q3_OPTIONS[q3].label;

  if (
    q1Label === "Сопротивляется новому" &&
    q2Labels.includes("Перед сном") &&
    q3Label === "Чтобы стало спокойнее"
  ) {
    scores.adaptation += 2;
  }

  if (q1Label === "Часто грустит" && q2Labels.includes("При расставании")) {
    scores.grief += 2;
  }

  if (
    q1Label === "Ревнует" &&
    q2Labels.includes("Дома с родителями") &&
    q3Label === "Чтобы в семье стало мягче"
  ) {
    scores.jealousy += 2;
  }

  const sorted = (Object.entries(scores) as [ArchetypeId, number][]).sort(
    (a, b) => b[1] - a[1]
  );

  const primary = sorted[0][0];
  const secondary =
    sorted[0][1] - sorted[1][1] < 3 ? sorted[1][0] : null;

  return { primary, secondary };
}

/** Resolve Q2 option color: if Q1 selected a secondary archetype, use that color instead */
function getQ2Color(optIndex: number, q1Selected: number | null): ArchetypeId {
  const map = Q2_COLOR_MAP[optIndex];
  if (q1Selected !== null) {
    const q1Arch = Q1_ARCHETYPE_MAP[q1Selected];
    // If Q1's archetype matches the secondary of this Q2 option, use that color
    if (map.secondary && map.secondary === q1Arch) {
      return q1Arch;
    }
  }
  return map.primary;
}

function colorVar(id: ArchetypeId) {
  return `var(--archetype-${id})`;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (goal: string) => void;
}

const ArchetypeQuiz = ({ open, onOpenChange, onSelect }: Props) => {
  const [step, setStep] = useState(0);
  const [q1, setQ1] = useState<number | null>(null);
  const [q2, setQ2] = useState<number[]>([]);
  const [q3, setQ3] = useState<number | null>(null);
  const [result, setResult] = useState<{
    primary: ArchetypeId;
    secondary: ArchetypeId | null;
  } | null>(null);

  const reset = () => {
    setStep(0);
    setQ1(null);
    setQ2([]);
    setQ3(null);
    setResult(null);
  };

  const handleOpenChange = (v: boolean) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const handleQ1 = (idx: number) => {
    setQ1(idx);
    setStep(1);
  };

  const toggleQ2 = (idx: number) => {
    setQ2((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  };

  const handleQ2Next = () => {
    if (q2.length > 0) setStep(2);
  };

  const handleQ3 = (idx: number) => {
    setQ3(idx);
    const res = computeResult(q1!, q2, idx);
    setResult(res);
    setStep(3);
  };

  const handleSelect = (id: ArchetypeId) => {
    handleOpenChange(false);
    onSelect(id);
  };

  const stepTitles = [
    "Вопрос 1 из 3",
    "Вопрос 2 из 3",
    "Вопрос 3 из 3",
    "Результат",
  ];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="rounded-3xl max-w-md max-h-[90vh] overflow-y-auto custom-scrollbar">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">
            {step < 3 ? "Помочь выбрать" : "Мы подобрали историю"}
          </DialogTitle>
          <DialogDescription className="font-sans text-sm text-muted-foreground">
            {stepTitles[step]}
          </DialogDescription>
        </DialogHeader>

        {/* Progress bar */}
        <div className="flex gap-1.5">
          {[0, 1, 2].map((s) => (
            <div
              key={s}
              className={`h-1 flex-1 rounded-full transition-colors ${
                s <= step ? "bg-primary" : "bg-border"
              }`}
            />
          ))}
        </div>

        <AnimatePresence mode="wait">
          {/* Q1 — hover & select with archetype colors */}
          {step === 0 && (
            <motion.div
              key="q1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-3 pt-2"
            >
              <p className="font-sans text-base text-foreground font-medium">
                Что вы замечаете у ребёнка в последнее время?
              </p>
              <div className="space-y-2">
                {Q1_OPTIONS.map((opt, i) => {
                  const archId = Q1_ARCHETYPE_MAP[i];
                  const isSelected = q1 === i;
                  const c = colorVar(archId);
                  return (
                    <button
                      key={i}
                      onClick={() => handleQ1(i)}
                      className="w-full text-left px-4 py-3 rounded-2xl border font-sans text-sm text-foreground transition-all flex items-center gap-3 group/opt"
                      style={
                        isSelected
                          ? {
                              borderColor: `hsl(${c})`,
                              backgroundColor: `hsl(${c} / 0.08)`,
                              borderLeftWidth: "3px",
                            }
                          : {
                              borderColor: `hsl(var(--border))`,
                              backgroundColor: `hsl(var(--card))`,
                            }
                      }
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.borderColor = `hsl(${c} / 0.4)`;
                          e.currentTarget.style.backgroundColor = `hsl(${c} / 0.04)`;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.borderColor = `hsl(var(--border))`;
                          e.currentTarget.style.backgroundColor = `hsl(var(--card))`;
                        }
                      }}
                    >
                      <span
                        className="w-2 h-2 rounded-full shrink-0"
                        style={{ backgroundColor: `hsl(${c})` }}
                      />
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Q2 (multi) — context-aware archetype colors */}
          {step === 1 && (
            <motion.div
              key="q2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-3 pt-2"
            >
              <p className="font-sans text-base text-foreground font-medium">
                В каких ситуациях это проявляется сильнее всего?
              </p>
              <p className="font-sans text-xs text-muted-foreground">
                Можно выбрать несколько вариантов
              </p>
              <div className="space-y-2">
                {Q2_OPTIONS.map((opt, i) => {
                  const archId = getQ2Color(i, q1);
                  const c = colorVar(archId);
                  const isSelected = q2.includes(i);
                  return (
                    <button
                      key={i}
                      onClick={() => toggleQ2(i)}
                      className="w-full text-left px-4 py-3 rounded-2xl border font-sans text-sm text-foreground transition-all flex items-center gap-3"
                      style={
                        isSelected
                          ? {
                              borderColor: `hsl(${c})`,
                              backgroundColor: `hsl(${c} / 0.08)`,
                              borderLeftWidth: "3px",
                            }
                          : {
                              borderColor: `hsl(var(--border))`,
                              backgroundColor: `hsl(var(--card))`,
                            }
                      }
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.borderColor = `hsl(${c} / 0.4)`;
                          e.currentTarget.style.backgroundColor = `hsl(${c} / 0.04)`;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.borderColor = `hsl(var(--border))`;
                          e.currentTarget.style.backgroundColor = `hsl(var(--card))`;
                        }
                      }}
                    >
                      <span
                        className="w-2 h-2 rounded-full shrink-0"
                        style={{ backgroundColor: `hsl(${c})` }}
                      />
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                onClick={handleQ2Next}
                disabled={q2.length === 0}
                className="w-full py-3 rounded-2xl bg-primary text-primary-foreground font-sans font-semibold text-base transition-shadow hover:shadow-lg disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Далее
              </motion.button>
            </motion.div>
          )}

          {/* Q3 — archetype colors with hover */}
          {step === 2 && (
            <motion.div
              key="q3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-3 pt-2"
            >
              <p className="font-sans text-base text-foreground font-medium">
                Какого результата вам хотелось бы?
              </p>
              <div className="space-y-2">
                {Q3_OPTIONS.map((opt, i) => {
                  const archId = Q3_ARCHETYPE_MAP[i];
                  const c = colorVar(archId);
                  const isSelected = q3 === i;
                  return (
                    <button
                      key={i}
                      onClick={() => handleQ3(i)}
                      className="w-full text-left px-4 py-3 rounded-2xl border font-sans text-sm text-foreground transition-all flex items-center gap-3"
                      style={
                        isSelected
                          ? {
                              borderColor: `hsl(${c})`,
                              backgroundColor: `hsl(${c} / 0.08)`,
                              borderLeftWidth: "3px",
                            }
                          : {
                              borderColor: `hsl(var(--border))`,
                              backgroundColor: `hsl(var(--card))`,
                            }
                      }
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.borderColor = `hsl(${c} / 0.4)`;
                          e.currentTarget.style.backgroundColor = `hsl(${c} / 0.04)`;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.borderColor = `hsl(var(--border))`;
                          e.currentTarget.style.backgroundColor = `hsl(var(--card))`;
                        }
                      }}
                    >
                      <span
                        className="w-2 h-2 rounded-full shrink-0"
                        style={{ backgroundColor: `hsl(${c})` }}
                      />
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Result — with archetype color glow */}
          {step === 3 && result && (
            <motion.div
              key="result"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="space-y-4 pt-2"
            >
              {/* Primary */}
              <div
                className="rounded-2xl border-2 p-5 space-y-2"
                style={{
                  borderColor: `hsl(${colorVar(result.primary)})`,
                  backgroundColor: `hsl(${colorVar(result.primary)} / 0.06)`,
                  boxShadow: `0 4px 20px -4px hsl(${colorVar(result.primary)} / 0.2)`,
                }}
              >
                <p className="font-sans text-xs font-medium uppercase tracking-wide flex items-center gap-1.5"
                   style={{ color: `hsl(${colorVar(result.primary)})` }}
                >
                  {ARCHETYPE_EMOJIS[result.primary]} Рекомендуем
                </p>
                <h3 className="font-serif text-lg text-foreground font-semibold">
                  {ARCHETYPE_LABELS[result.primary]}
                </h3>
                <p className="font-sans text-sm text-muted-foreground">
                  {ARCHETYPE_SUBTITLES[result.primary]}
                </p>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => handleSelect(result.primary)}
                  className="w-full py-3 rounded-2xl bg-primary text-primary-foreground font-sans font-semibold text-base transition-shadow hover:shadow-lg mt-2"
                >
                  Выбрать эту тему
                </motion.button>
              </div>

              {/* Secondary */}
              {result.secondary && (
                <div
                  className="rounded-2xl border p-5 space-y-2"
                  style={{
                    borderColor: `hsl(${colorVar(result.secondary)} / 0.5)`,
                    backgroundColor: `hsl(${colorVar(result.secondary)} / 0.04)`,
                  }}
                >
                  <p className="font-sans text-xs text-muted-foreground font-medium uppercase tracking-wide flex items-center gap-1.5">
                    {ARCHETYPE_EMOJIS[result.secondary]} Альтернатива
                  </p>
                  <h3 className="font-serif text-lg text-foreground font-semibold">
                    {ARCHETYPE_LABELS[result.secondary]}
                  </h3>
                  <p className="font-sans text-sm text-muted-foreground">
                    {ARCHETYPE_SUBTITLES[result.secondary]}
                  </p>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => handleSelect(result.secondary!)}
                    className="w-full py-3 rounded-2xl bg-secondary text-secondary-foreground font-sans font-semibold text-base transition-shadow hover:shadow-md mt-2"
                  >
                    Выбрать эту тему
                  </motion.button>
                </div>
              )}

              <button
                onClick={reset}
                className="w-full text-center text-sm font-sans text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors pt-1"
              >
                Пройти заново
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};

export default ArchetypeQuiz;
